import {
  AnchorButton,
  ButtonGroup,
  Intent,
  Menu,
  MenuItem,
  Position,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { emptyObject } from 'vet-bones/constants';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { FileExtension } from 'src/constants/fileExtensions';
import { TEST_IDS } from 'src/constants/testIds';
import { Project } from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { duplicateProject } from 'src/utils/duplicate/duplicateProject';
import { decodeProject } from 'src/utils/exportProject/encodeProject';
import { generateDateTimeId } from 'src/utils/generateId';
import { useNavigateProject } from 'src/utils/hooks/useNavigateProject';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { readFile } from 'src/utils/readFile';

type newProjectButtonProps = {
  isImportAllowed: boolean;
};

export const NewProjectButton: React.VFC<newProjectButtonProps> = ({
  isImportAllowed,
}) => {
  const dispatch = useReduxDispatch();
  const navigateProject = useNavigateProject();

  const onDropAccepted = async (files: File[]) => {
    try {
      const [file] = files;
      const contents = await readFile(file);

      const data = decodeProject(contents);
      const project = Object.values(
        data?.projects?.projectsById || emptyObject
      )[0] as Project;

      if (!project) {
        return;
      }

      const newProject = duplicateProject(project, {
        id: generateDateTimeId(),
        modelNamespace: project.modelNamespace,
        dataNamespace: project.dataNamespace,
      });

      dispatch(projectsActionCreators.importedProjectFromFile(newProject));
      navigateProject(newProject.id);
    } catch {
      dispatch(projectsActionCreators.importedInvalidProjectFromFile());
    }
  };

  const { getInputProps, open } = useDropzone({
    accept: [FileExtension.STARDOG_DESIGNER],
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDropAccepted,
  });

  return (
    <ButtonGroup className="sd-button-group-data">
      <AnchorButton
        data-testid={TEST_IDS.home.createProjectButton}
        icon={isImportAllowed ? IconNames.PLUS : null}
        intent={Intent.PRIMARY}
        onClick={() => {
          dispatch(
            uiSlice.actions.toggledCreateProjectDialog({ isOpen: true })
          );
        }}
        text={
          isImportAllowed
            ? copy.components.home.new
            : copy.components.home.newProject
        }
      />

      {isImportAllowed ? (
        <>
          <input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...getInputProps()}
            data-testid={TEST_IDS.home.importProject.fileInput}
          />

          <Popover2
            content={
              <Menu>
                <MenuItem
                  data-testid={TEST_IDS.home.importProject.button}
                  onClick={open}
                  text={copy.components.home.importProject}
                />
              </Menu>
            }
            position={Position.BOTTOM_LEFT}
          >
            <AnchorButton
              data-testid={TEST_IDS.home.createMenuButton}
              icon={IconNames.CARET_DOWN}
              intent={Intent.PRIMARY}
            />
          </Popover2>
        </>
      ) : null}
    </ButtonGroup>
  );
};
